import React, { useState, useEffect, useCallback } from 'react';
import { RSVPResponse } from '../types/rsvp';
import { validateRSVPForm, validateRSVPFormAsync, ValidationErrors, getInvitationType } from '../utils/validation';
import { saveRSVPResponse, getCurrentRSVP, storeCurrentRSVP, clearCurrentRSVP, checkExistingRSVP } from '../utils/storage';
import AttendingConfirmation from './AttendingConfirmation';
import NotAttendingConfirmation from './NotAttendingConfirmation';
// import GuestNameHelper from './GuestNameHelper'; // SECURITY: Disabled to prevent guest name exposure
import { OptimizedFloralCard, OptimizedFloralHeader } from './ui/OptimizedComponents';
import { FloralInput, FloralButton, FloralRadioGroup } from './ui/FormComponents';
import { FloralErrorBoundary } from './ui/FloralCard';
import { Users, Phone, Heart, ThumbsUp, ThumbsDown, Check, AlertCircle, CheckCircle, Search, Key } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

type Language = 'english' | 'iban';

interface RSVPFormProps {
  onSubmitSuccess: () => void;
  selectedLanguage: Language;
}

const RSVPForm: React.FC<RSVPFormProps> = ({ onSubmitSuccess, selectedLanguage }) => {
  const [formData, setFormData] = useState({
    guestName: '',
    phone: '',
    guestCount: 1,
    adultCount: 1,
    childCount: 0,
    attending: true,
    weddingCode: '',
    confirmSingleAttendance: false
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean | null>(null);
  const [existingRSVP, setExistingRSVP] = useState<RSVPResponse | null>(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [fieldValidation, setFieldValidation] = useState<Record<string, 'valid' | 'invalid' | 'checking' | null>>({});
  const [realTimeErrors, setRealTimeErrors] = useState<ValidationErrors>({});
  const [lookupCode, setLookupCode] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [showLookupModal, setShowLookupModal] = useState(false);



  const handleStartNewRSVP = () => {
    clearCurrentRSVP();
    setExistingRSVP(null);
    setShowConfirmation(null);
    setFormData({
      guestName: '',
      phone: '',
      guestCount: 1,
      adultCount: 1,
      childCount: 0,
      attending: true,
      weddingCode: '',
      confirmSingleAttendance: false
    });
    setErrors({});
    setLookupCode('');
    setLookupError('');
    setShowLookupModal(false);
  };

  const handleLookupRSVP = async () => {
    if (!lookupCode.trim()) {
      setLookupError(selectedLanguage === 'iban' ? 'Sila masukkan kod tikah' : 'Please enter a wedding code');
      return;
    }

    setIsLookingUp(true);
    setLookupError('');

    try {
      const existingRSVP = await checkExistingRSVP(undefined, lookupCode.trim());
      
      if (existingRSVP) {
        storeCurrentRSVP(existingRSVP);
        setExistingRSVP(existingRSVP);
        setShowConfirmation(existingRSVP.attending);
      } else {
        setLookupError(selectedLanguage === 'iban' ? 
          'Kod tikah enda sah. Peda baru kod nuan tauka gaga baru.' : 
          'Wedding code not found. Please check the code or create a new RSVP.');
      }
    } catch (error) {
      // Silent error handling - lookup failed
      setLookupError(selectedLanguage === 'iban' ? 
        'Ada masalah semasa mencari kod. Sila cuba lagi.' : 
        'Error occurred while looking up the code. Please try again.');
    } finally {
      setIsLookingUp(false);
    }
  };

  // Check for existing RSVP on component mount
  useEffect(() => {
    const checkForExistingRSVP = () => {
      const currentRSVP = getCurrentRSVP();
      if (currentRSVP) {
        setExistingRSVP(currentRSVP);
        setFormData({
          guestName: currentRSVP.guestName,
          phone: currentRSVP.phone,
          guestCount: currentRSVP.guestCount,
          adultCount: currentRSVP.adultCount,
          childCount: currentRSVP.childCount,
          attending: currentRSVP.attending,
          weddingCode: currentRSVP.weddingCode,
          confirmSingleAttendance: currentRSVP.confirmSingleAttendance || false
        });
        setShowConfirmation(currentRSVP.attending);
      }
      setIsCheckingExisting(false);
    };

    checkForExistingRSVP();
  }, []);

  // Debounced validation for real-time feedback
  const debounceValidation = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (field: string, value: any, formData: any) => {
        clearTimeout(timeoutId);
        setFieldValidation(prev => ({ ...prev, [field]: 'checking' }));

        timeoutId = setTimeout(async () => {
          try {
            const testData = { ...formData, [field]: value };
            const validationErrors = field === 'phone'
              ? await validateRSVPFormAsync(testData)
              : validateRSVPForm(testData);

            const hasError = validationErrors[field as keyof ValidationErrors];
            setFieldValidation(prev => ({
              ...prev,
              [field]: hasError ? 'invalid' : 'valid'
            }));
            setRealTimeErrors(prev => ({
              ...prev,
              [field]: validationErrors[field as keyof ValidationErrors]
            }));
          } catch (error) {
            setFieldValidation(prev => ({ ...prev, [field]: 'invalid' }));
          }
        }, 500);
      };
    })()
  , []);

  // Real-time validation for specific fields
  const validateNameFormat = (name: string): string | null => {
    if (!name.trim()) return null;

    // Check for invalid characters
    if (!/^[a-zA-Z\s,&'-]+$/.test(name)) {
      return 'Names can only contain letters, spaces, commas, ampersands, and apostrophes';
    }

    // Check for proper name format
    const names = name.trim().split(/[,\n]+/).filter(n => n.trim().length > 0);
    for (const singleName of names) {
      const cleanName = singleName.trim();
      if (cleanName.length < 2) {
        return 'Each name must be at least 2 characters long';
      }
      if (!/^[A-Z]/.test(cleanName)) {
        return 'Names should start with a capital letter';
      }
    }

    return null;
  };

  const validatePhoneFormat = (phone: string): string | null => {
    if (!phone.trim()) return null;

    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');

    // Check basic format
    if (!/^\+?[\d]{8,15}$/.test(cleaned)) {
      return 'Phone number should be 8-15 digits, optionally starting with +';
    }

    // Check Malaysian format specifically
    if (cleaned.startsWith('+60') || cleaned.startsWith('60')) {
      const withoutCountryCode = cleaned.replace(/^(\+?60)/, '');
      if (!/^[1-9]\d{7,9}$/.test(withoutCountryCode)) {
        return 'Invalid Malaysian phone number format';
      }
    }

    return null;
  };

  const validateWeddingCodeFormat = (code: string): string | null => {
    if (!code.trim()) return null;

    // Check basic format
    if (!/^[a-z]+\d{2}$/.test(code.toLowerCase())) {
      return 'Wedding code should be letters followed by 2 digits (e.g., josiah25)';
    }

    return null;
  };

  // Enhanced name parsing to detect multiple guests
  const parseGuestNames = (nameString: string): number => {
    if (!nameString || !nameString.trim()) return 0;

    const cleanedString = nameString.trim();

    // Split by explicit separators: comma, newline, semicolon
    let names = cleanedString.split(/[,;\n]+/).filter(name => name.trim().length > 0);

    // If multiple names found with explicit separators, return that count
    if (names.length > 1) {
      return names.length;
    }

    // If only one "name" after separator split, check for other patterns
    if (names.length === 1) {
      const singleName = names[0].trim();

      // Check for explicit "and" or "&" patterns
      if (/\s+(and|&)\s+/i.test(singleName)) {
        const splitNames = singleName.split(/\s+(and|&)\s+/i).filter(name => name.trim().length > 0);
        return Math.max(1, splitNames.length);
      }

      // Since guest list is first-name-only basis, treat space-separated words as individual first names
      // "Peter Cecilia" = 2 people (Peter and Cecilia)
      // "Peter Cecilia John" = 3 people (Peter, Cecilia, and John)
      const words = singleName.split(/\s+/).filter(word => word.trim().length > 0);

      // Each word represents a first name
      return Math.max(1, words.length);
    }

    // Fallback to 1 if no names found
    return 1;
  };

  const handleInputChange = (field: string, value: any) => {
    const updatedFormData = { ...formData, [field]: value };

    // Update total guest count when adult or child count changes
    if (field === 'adultCount' || field === 'childCount') {
      updatedFormData.guestCount = (updatedFormData.adultCount || 0) + (updatedFormData.childCount || 0);
    }

    // Auto-detect guest count based on names entered
    if (field === 'guestName') {
      const nameCount = parseGuestNames(value);
      if (nameCount > 0) {
        updatedFormData.guestCount = nameCount;
        updatedFormData.adultCount = nameCount;
        updatedFormData.childCount = 0;
      }

      // Real-time name format validation
      const nameError = validateNameFormat(value);
      setRealTimeErrors(prev => ({ ...prev, guestName: nameError }));
      setFieldValidation(prev => ({ ...prev, guestName: nameError ? 'invalid' : 'valid' }));
    }

    // Real-time phone validation
    if (field === 'phone') {
      const phoneError = validatePhoneFormat(value);
      setRealTimeErrors(prev => ({ ...prev, phone: phoneError }));
      if (!phoneError) {
        // Only check for duplicates if format is valid
        debounceValidation('phone', value, updatedFormData);
      } else {
        setFieldValidation(prev => ({ ...prev, phone: 'invalid' }));
      }
    }

    // Real-time wedding code validation
    if (field === 'weddingCode') {
      const codeError = validateWeddingCodeFormat(value);
      setRealTimeErrors(prev => ({ ...prev, weddingCode: codeError }));
      if (!codeError) {
        // Check against valid codes
        debounceValidation('weddingCode', value, updatedFormData);
      } else {
        setFieldValidation(prev => ({ ...prev, weddingCode: 'invalid' }));
      }
    }

    setFormData(updatedFormData);

    // Clear error when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Clear real-time error when field becomes empty
    if (!value || (typeof value === 'string' && !value.trim())) {
      setRealTimeErrors(prev => ({ ...prev, [field]: undefined }));
      setFieldValidation(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = (): boolean => {
    const validationErrors = validateRSVPForm(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Validation indicator component
  const ValidationIndicator: React.FC<{ field: string; className?: string }> = ({ field, className = '' }) => {
    const status = fieldValidation[field];
    const error = realTimeErrors[field as keyof ValidationErrors];

    if (!status) return null;

    return (
      <div className={`flex items-center ${className}`}>
        {status === 'checking' && (
          <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
        )}
        {status === 'valid' && (
          <CheckCircle className="w-4 h-4 text-green-500" />
        )}
        {status === 'invalid' && (
          <AlertCircle className="w-4 h-4 text-red-500" />
        )}
        {error && (
          <span className="ml-2 text-xs text-red-600">{error}</span>
        )}
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Use async validation to check for duplicates
    const validationErrors = await validateRSVPFormAsync(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Generate a random email for each submission since it's required by the API
      // but we don't need to collect it from guests
      const randomEmail = `guest_${Date.now()}${Math.random().toString(36).substring(2, 8)}@example.com`;
      
      const response: RSVPResponse = {
        id: `rsvp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        guestName: formData.guestName,
        email: randomEmail, // Add the random email
        phone: formData.phone,
        guestCount: formData.guestCount,
        adultCount: formData.adultCount,
        childCount: formData.childCount,
        weddingCode: formData.weddingCode,
        submittedAt: new Date(),
        attending: formData.attending,
        invitationType: getInvitationType(formData.weddingCode),
        confirmSingleAttendance: formData.confirmSingleAttendance
      };
    
      await saveRSVPResponse(response);
      
      storeCurrentRSVP(response);
      setExistingRSVP(response);
      setIsSubmitting(false);
      setShowConfirmation(formData.attending);
      onSubmitSuccess();
    } catch (error) {
      // Silent error handling - RSVP submission failed
      setIsSubmitting(false);
      setErrors({ submit: 'Failed to submit RSVP. Please try again.' });
    }
  };

  // Show loading state while checking for existing RSVP
  if (isCheckingExisting) {
    return (
      <FloralErrorBoundary>
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 relative flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-sage-300 border-t-sage-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sage-600">Checking for existing RSVP...</p>
          </div>
        </div>
      </FloralErrorBoundary>
    );
  }

  // Show confirmation page if form was submitted or existing RSVP found
  if (showConfirmation !== null) {
    const rsvpData = existingRSVP || formData;
    const invitationType = existingRSVP?.invitationType || getInvitationType(formData.weddingCode);
    return rsvpData.attending ? (
      <AttendingConfirmation
        guestName={rsvpData.guestName}
        guestCount={rsvpData.guestCount}
        adultCount={rsvpData.adultCount}
        childCount={rsvpData.childCount}
        invitationType={invitationType}
        onStartNewRSVP={handleStartNewRSVP}
        selectedLanguage={selectedLanguage}
      />
    ) : (
      <NotAttendingConfirmation
        guestName={rsvpData.guestName}
        onStartNewRSVP={handleStartNewRSVP}
        selectedLanguage={selectedLanguage}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-dusty-rose-50">
      <FloralErrorBoundary>
        <div className="py-12 px-4 sm:px-6 lg:px-8 relative">
          {/* Corner decorations with flowers */}
          <div className="floral-corner-decoration floral-corner-top-left"></div>
          <div className="floral-corner-decoration floral-corner-top-right"></div>
          <div className="floral-corner-decoration floral-corner-bottom-left"></div>
          <div className="floral-corner-decoration floral-corner-bottom-right"></div>

        <div className="max-w-3xl mx-auto relative z-10" style={{position: 'relative', zIndex: 10}}>
          <OptimizedFloralHeader
            bride="Dominic"
            groom="Kemberly"
            date={selectedLanguage === 'iban' ? '1 november, 2025 hari enam' : 'november 1, 2025 • saturday'}
            className="mb-8 sm:mb-12"
            showDivider={false}
            selectedLanguage={selectedLanguage}
          />

          {/* Decorative divider */}
          <div className="floral-divider"></div>

          {/* RSVP Form */}
          <OptimizedFloralCard
            className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl relative z-20"
            variant="elevated"
            showCornerDecorations={false}
            aria-label="Wedding RSVP Form"
          >
            <h2 className="floral-title text-2xl sm:text-3xl text-center mb-2 sm:mb-3">
              {selectedLanguage === 'iban' ? 'Sahka Penatai Nuan' : 'Please RSVP'}
            </h2>
            <p className="floral-subtitle text-center mb-6 sm:mb-8">
              {selectedLanguage === 'iban' ? 'sebedau 30 September, 2025' : 'by September 30th, 2025'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="inline w-4 h-4 mr-2 text-green-500" />
                  {selectedLanguage === 'iban' ? 'Engkahka Nama Keterubah Nuan *' : 'First Name(s) of All Attending Guests *'}
                </label>
                <div className="relative">
                  <FloralInput
                    value={formData.guestName}
                    onChange={(value) => handleInputChange('guestName', value)}
                    placeholder="Enter first names of all guests"
                    required
                    error={errors.guestName}
                  />
                  <ValidationIndicator field="guestName" className="absolute right-3 top-1/2 transform -translate-y-1/2" />
                </div>
                {realTimeErrors.guestName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {realTimeErrors.guestName}
                  </p>
                )}

                {/* SECURITY: Guest Name Helper disabled to prevent unauthorized guest name exposure */}
                <div className="mt-1 text-xs text-gray-500">
                  <p>{selectedLanguage === 'iban' ? 'Kena meh nama keterubah semua temuai ke dibai datai.' : 'Please include the first names of all guests who will be attending.'}</p>
                  <p className="mt-1 bg-blue-50 p-2 rounded border border-blue-100 text-blue-700">
                    <strong>{selectedLanguage === 'iban' ? 'Chunto:' : 'Examples:'}</strong><br/>
                    {selectedLanguage === 'iban' ? (
                      <>
                        • Enti nuan siku aja: "Alex"<br/>
                        • Enti seduai melaki bini: "Thomas Lisa" tauka "Thomas & Lisa"<br/>
                        • Enti kita ruang bilik: "David Emma Stacy John"
                      </>
                    ) : (
                      <>
                        • Single guest: "Sarah"<br/>
                        • Couple: "Michael Lisa" or "Michael & Lisa"<br/>
                        • Family: "David Emma Alex Sophie"
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="inline w-4 h-4 mr-2 text-green-500" />
                  {selectedLanguage === 'iban' ? 'Lumur Talipun*' : 'Phone Number *'}
                </label>
                <div className="relative">
                  <FloralInput
                    type="tel"
                    value={formData.phone}
                    onChange={(value) => handleInputChange('phone', value)}
                    placeholder="(60) 123-4567"
                    required
                    error={errors.phone}
                  />
                  <ValidationIndicator field="phone" className="absolute right-3 top-1/2 transform -translate-y-1/2" />
                </div>
                {realTimeErrors.phone && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {realTimeErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedLanguage === 'iban' ? 'Kati nuan ulih datai?*' : 'Will you be attending? *'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={() => handleInputChange('attending', true)}
                    className={`flex items-center justify-center px-4 py-3 rounded-xl border-2 transition-all ${
                      formData.attending
                        ? 'bg-green-50 border-green-300 text-green-700'
                        : 'border-gray-200 text-gray-500 hover:border-green-200 hover:bg-green-50/50'
                    }`}
                  >
                    <ThumbsUp className="w-5 h-5 mr-2" />
                    {selectedLanguage === 'iban' ? 'Au! Aku ulih datai' : "Yes, I'll be there"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('attending', false)}
                    className={`flex items-center justify-center px-4 py-3 rounded-xl border-2 transition-all ${
                      formData.attending === false
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 text-gray-500 hover:border-blue-200 hover:bg-blue-50/50'
                    }`}
                  >
                    <ThumbsDown className="w-5 h-5 mr-2" />
                    {selectedLanguage === 'iban' ? 'Minta ampun! Aku enda ulih datai' : "Sorry, I can't attend"}
                  </button>
                </div>
              </div>



            {/* Attending alone message */}
            {formData.attending && formData.guestCount === 1 && (
              <div className="bg-green-50/50 rounded-xl p-4 border border-green-100">
                <p className="text-sm text-green-700 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  {selectedLanguage === 'iban' ? 'Nuan datai siku aja' : "You're attending alone"}
                </p>
              </div>
            )}

            {/* Guest Count - Only show if attending and more than 1 guest */}
            {formData.attending && formData.guestCount > 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="inline w-4 h-4 mr-2 text-blue-500" />
                    {selectedLanguage === 'iban' ? 'Penerang pasal Temuai*' : 'Guest Information *'}
                  </label>
                  <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                    <p className="text-sm text-blue-700 mb-3">
                      {selectedLanguage === 'iban' ? `Penyampau Temuai: ${formData.guestCount} iku` : `Total Guests: ${formData.guestCount} ${formData.guestCount === 1 ? 'person' : 'people'}`}
                    </p>

                    {/* Adult Count */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {selectedLanguage === 'iban' ? 'Orang Dewasa' : 'Adults'}
                      </label>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => handleInputChange('adultCount', Math.max(1, formData.adultCount - 1))}
                          className="w-10 h-10 rounded-l-lg bg-blue-100 hover:bg-blue-200 flex items-center justify-center text-blue-700 border border-blue-200"
                        >
                          -
                        </button>
                        <div className="h-10 px-4 flex items-center justify-center bg-white border-t border-b border-blue-200 min-w-[60px] text-center">
                          {formData.adultCount}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleInputChange('adultCount', Math.min(6, formData.adultCount + 1))}
                          className="w-10 h-10 rounded-r-lg bg-blue-100 hover:bg-blue-200 flex items-center justify-center text-blue-700 border border-blue-200"
                        >
                          +
                        </button>
                        <span className="ml-3 text-sm text-gray-500">{selectedLanguage === 'iban' ? 'Patut diisi' : 'Required'}</span>
                      </div>
                      {errors.adultCount && (
                        <p className="mt-1 text-sm text-red-600">{errors.adultCount}</p>
                      )}
                    </div>

                    {/* Child Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {selectedLanguage === 'iban' ? 'Anak mit (baruh umur 12 taun)' : 'Children (under 12)'}
                      </label>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => handleInputChange('childCount', Math.max(0, formData.childCount - 1))}
                          className="w-10 h-10 rounded-l-lg bg-green-100 hover:bg-green-200 flex items-center justify-center text-green-700 border border-green-200"
                        >
                          -
                        </button>
                        <div className="h-10 px-4 flex items-center justify-center bg-white border-t border-b border-green-200 min-w-[60px] text-center">
                          {formData.childCount}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleInputChange('childCount', Math.min(5, formData.childCount + 1))}
                          className="w-10 h-10 rounded-r-lg bg-green-100 hover:bg-green-200 flex items-center justify-center text-green-700 border border-green-200"
                        >
                          +
                        </button>
                        <span className="ml-3 text-sm text-gray-500">{selectedLanguage === 'iban' ? 'Tau diisi enti ka' : 'Optional'}</span>
                      </div>
                      {errors.childCount && (
                        <p className="mt-1 text-sm text-red-600">{errors.childCount}</p>
                      )}
                    </div>
                  </div>
                  {errors.guestCount && (
                    <p className="mt-1 text-sm text-red-600">{errors.guestCount}</p>
                  )}
                </div>
              </div>
            )}

            {/* Single Attendance Confirmation - Only show when there's a warning */}
            {errors.singleAttendanceWarning && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 mb-3">{errors.singleAttendanceWarning}</p>
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.confirmSingleAttendance}
                        onChange={(e) => handleInputChange('confirmSingleAttendance', e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-amber-600 focus:ring-amber-500 border-amber-300 rounded"
                      />
                      <span className="text-sm text-amber-800">
                         Yes, I confirm that only one person from our couple is attending
                       </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Wedding Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {selectedLanguage === 'iban' ? 'Kod Tikah *' : 'Wedding Code *'}
                </label>
                <div className="relative">
                  <FloralInput
                    type="text"
                    value={formData.weddingCode}
                    onChange={(value) => handleInputChange('weddingCode', value)}
                    placeholder="Enter the wedding code"
                    required
                    error={errors.weddingCode}
                  />
                  <ValidationIndicator field="weddingCode" className="absolute right-3 top-1/2 transform -translate-y-1/2" />
                </div>
                {realTimeErrors.weddingCode && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {realTimeErrors.weddingCode}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {selectedLanguage === 'iban' ? 'Tepuka kod ke udah disediaka dalam surat mai datai ke udah diberi ngagai nuan' : 'Please enter the code provided in your invitation'}
                </p>
              </div>


              <div className="pt-4 sm:pt-6">
                <FloralButton
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  className="w-full"
                  size="md"
                >
                  <span className="flex items-center justify-center">
                    <Check className="w-4 h-4 mr-2" />
                    {formData.attending ? (selectedLanguage === 'iban' ? 'Sahka Penatai' : 'Confirm Attendance') : 'Send Response'}
                  </span>
                </FloralButton>
                
                {/* Already RSVP'd Link */}
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => setShowLookupModal(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline transition-colors"
                  >
                    {selectedLanguage === 'iban' ? 'Udah ngesahka penatai? Tekan ditu.' : 'Already RSVP\'d? Click here'}
                  </button>
                </div>
              </div>
            </form>
          </OptimizedFloralCard>

          {/* Wedding Code Lookup Modal */}
          {showLookupModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
              <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl border border-white/20 max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
                {/* Header with decorative elements */}
                <div className="relative p-8 pb-6">
                  {/* Decorative corner elements */}
                  <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-green-200 rounded-tl-2xl opacity-60"></div>
                  <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-green-200 rounded-tr-2xl opacity-60"></div>
                  
                  {/* Close button */}
                  <button
                    onClick={() => {
                      setShowLookupModal(false);
                      setLookupCode('');
                      setLookupError('');
                    }}
                    className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
                    aria-label="Close modal"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Icon and title */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-50 to-green-50 rounded-full mb-4 shadow-sm">
                      <Key className="w-8 h-8 text-blue-600" />
                    </div>
                    <h2 className="floral-title text-2xl sm:text-3xl text-gray-800 mb-2">
                      {selectedLanguage === 'iban' ? 'Tepuka Kod Tikah' : 'Find Your RSVP'}
                    </h2>
                    <p className="floral-subtitle text-gray-600 text-sm leading-relaxed">
                      {selectedLanguage === 'iban' ? 'KOD KE UDAH DISEDIAKA DALAM SURAT MAI DATAI KE UDAH DIBERI NGAGAI NUAN' : 'Enter the unique code from your wedding invitation'}
                    </p>
                  </div>
                </div>

                {/* Form content */}
                <div className="px-8 pb-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Key className="w-4 h-4 inline mr-2" />
                        {selectedLanguage === 'iban' ? 'Kod Tikah' : 'Wedding Code'}
                      </label>
                      <div className="relative">
                        <FloralInput
                          type="text"
                          value={lookupCode}
                          onChange={setLookupCode}
                          placeholder={selectedLanguage === 'iban' ? 'Contoh: WED2025-ABC123' : 'e.g., WED2025-ABC123'}
                          className="text-center text-lg font-mono tracking-wider"
                          required
                        />
                      </div>
                      {lookupError && lookupError.trim() && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <p className="text-sm text-red-700 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                            {lookupError}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                     <div className="flex flex-col sm:flex-row gap-3 pt-2">
                         <FloralButton
                           onClick={handleLookupRSVP}
                           disabled={isLookingUp || !lookupCode.trim()}
                           loading={isLookingUp}
                           className="flex-1"
                           size="md"
                         >
                           <Search className="w-4 h-4 mr-2" />
                           {selectedLanguage === 'iban' ? 'Tekan Enti Nuan Deka Meda Penerang Nuan' : 'Find My RSVP'}
                         </FloralButton>
                         <FloralButton
                           onClick={() => {
                             setShowLookupModal(false);
                             setLookupCode('');
                             setLookupError('');
                           }}
                           variant="outline"
                           size="md"
                         >
                           {selectedLanguage === 'iban' ? 'Kinsil' : 'Cancel'}
                         </FloralButton>
                       </div>
                  </div>
                </div>

                {/* Decorative bottom elements */}
                <div className="relative">
                  <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-green-200 rounded-bl-2xl opacity-60"></div>
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-green-200 rounded-br-2xl opacity-60"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </FloralErrorBoundary>
    </div>
  );
};

export default RSVPForm;
